import { useEffect, useState } from "react"
import { QRCodeSVG } from "qrcode.react"
import { X, Smartphone } from "lucide-react"
import styles from "./styles.module.scss"
import { cn } from "@/lib/utils"
import { useAutoTranslation } from "@/entities"
import { DISCOVERED_TRANSLATION_KEYS, LANG_KEYS } from "@/shared/constants"

const PROMO_STORAGE_KEY = "mobile-app-promo-dismissed"
const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000

// Store URLs
const APP_STORE_URL =
  "https://apps.apple.com/us/app/frateca-text-to-speech-audio/id6741859465"
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=ai.texttospeech.app"

// Use current origin for the QR code so it works in any environment
const getQRCodeURL = () => {
  if (typeof window !== "undefined") {
    return `${window.location.origin}/download-app`
  }
  return "https://frateca.com/download-app"
}

export const MobileAppPromo = () => {
  const { t } = useAutoTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimatingOut, setIsAnimatingOut] = useState(false)
  const [qrUrl, setQrUrl] = useState("https://frateca.com/download-app")

  useEffect(() => {
    // Set QR URL on client side
    setQrUrl(getQRCodeURL())

    // Check if we should show the popup
    const lastDismissed = localStorage.getItem(PROMO_STORAGE_KEY)
    const shouldShow =
      !lastDismissed || Date.now() - parseInt(lastDismissed) > WEEK_IN_MS

    if (shouldShow) {
      // Delay showing for better UX (let page load first)
      const timer = setTimeout(() => setIsVisible(true), 2000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleDismiss = () => {
    setIsAnimatingOut(true)
    localStorage.setItem(PROMO_STORAGE_KEY, Date.now().toString())

    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsVisible(false)
      setIsAnimatingOut(false)
    }, 300)
  }

  if (!isVisible) return null

  return (
    <div
      className={cn(styles.wrapper, {
        [styles.wrapper_animateOut]: isAnimatingOut,
      })}
    >
      {/* Close button */}
      <button
        type="button"
        className={styles.closeButton}
        onClick={handleDismiss}
        aria-label={t(LANG_KEYS.CLOSE)}
      >
        <X size={16} />
      </button>

      {/* Main content area */}
      <div className={styles.content}>
        {/* Left side - QR Code */}
        <div className={styles.qrSection}>
          <div className={styles.qrWrapper}>
            <QRCodeSVG
              value={qrUrl}
              size={100}
              level="L"
              includeMargin={false}
              bgColor="transparent"
              fgColor="currentColor"
              className={styles.qrCode}
            />
          </div>
        </div>

        {/* Right side - Text content */}
        <div className={styles.textSection}>
          <div className={styles.header}>
            <div className={styles.iconBadge}>
              <Smartphone size={14} />
            </div>
            <span className={styles.badge}>
              {t(DISCOVERED_TRANSLATION_KEYS.MOBILE_APP)}
            </span>
          </div>

          <h3 className={styles.title}>
            {t(DISCOVERED_TRANSLATION_KEYS.LISTEN_ON_THE_GO)}
          </h3>

          <p className={styles.subtitle}>
            {t(
              DISCOVERED_TRANSLATION_KEYS.SCAN_THE_QR_CODE_TO_DOWNLOAD_YOUR_LIBRARY_SYNCS_AUTOMATICALLY
            )}
          </p>

          <div className={styles.stores}>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.storeIcon}
              title={t(DISCOVERED_TRANSLATION_KEYS.DOWNLOAD_ON_APP_STORE)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.storeIcon}
              title={t(DISCOVERED_TRANSLATION_KEYS.GET_IT_ON_GOOGLE_PLAY)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Decorative gradient line at bottom */}
      <div className={styles.gradientLine} />
    </div>
  )
}
